import React, { useState, useEffect, useRef } from 'react';
import { centsToDisplay, displayToCents } from '../utils/price';

interface PriceInputProps {
  value: number | null | undefined;
  onChange: (cents: number | null) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  style?: React.CSSProperties;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * A controlled price input that stores cents internally but lets the user
 * freely edit the dollar string without round-trip formatting issues.
 * Commits cents value on blur.
 */
export function PriceInput({ value, onChange, className, placeholder = '0.00', disabled, readOnly, style, onFocus }: PriceInputProps) {
  const [localValue, setLocalValue] = useState(() =>
    value != null ? centsToDisplay(value) : ''
  );
  const isFocused = useRef(false);

  // Sync from parent when not focused (e.g. item switch, external update)
  useEffect(() => {
    if (!isFocused.current) {
      setLocalValue(value != null ? centsToDisplay(value) : '');
    }
  }, [value]);

  return (
    <input
      type="number"
      min={0}
      step="0.01"
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      style={style}
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
      }}
      onFocus={(e) => {
        isFocused.current = true;
        e.target.select();
        onFocus?.(e);
      }}
      onBlur={(e) => {
        isFocused.current = false;
        const cents = displayToCents(e.target.value);
        onChange(cents);
        // Re-format on blur
        setLocalValue(cents != null ? centsToDisplay(cents) : '');
      }}
    />
  );
}
